import { ArrowDown01Icon, ArrowDown10Icon, DatabaseIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Frame, FramePanel } from "@/components/ui/frame";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/utils/class-name";

/** Matches loading skeleton row inner height and flex layout. */
const TABLE_PLACEHOLDER_ROW_INNER = "box-border flex h-16 w-full flex-row items-center";

export type TableHeadingData = {
    props?: React.ComponentProps<typeof TableHead>;
    content: React.ReactNode;
};

export type TableRowData = {
    props?: React.ComponentProps<typeof TableRow>;
    cells: TableCellData[];
};

export type TableCellData = {
    props?: React.ComponentProps<typeof TableCell>;
    content: React.ReactNode;
};

// TODO(jeff): Implement - this is a placeholder for design/layout.
export function TablePagination() {
    return (
        <Button className={"w-full py-2"} variant={"outline"}>
            <DatabaseIcon className={"size-6"} /> Load more...
        </Button>
    );
}

// TODO(jeff): Implement sorting logic - this is a placeholder for design/layout.
export function SortableTableHeader({
    ascending = false,
    children,
}: {
    ascending?: boolean;
    children: React.ReactNode;
}) {
    return (
        <Button
            variant={"ghost"}
            className={"pl-0 border-0 w-full flex flex-row gap-2 items-center justify-start sm:text-lg"}
        >
            {children}{" "}
            {ascending ? (
                <ArrowDown01Icon className={"size-6 inline-block"} />
            ) : (
                <ArrowDown10Icon className={"size-6 inline-block"} />
            )}
        </Button>
    );
}

/**
 * A simple table abstraction component that provides a framed table with consistent styling.
 *
 * This component allows you to pass in structured heading, row, and cell data to render
 * a fully-featured table with loading states, optional pagination, and frame styling.
 * The table automatically handles column spanning and allows for flexible responsive layout via
 * specifying `props` on table elements.
 *
 * @param headings - Optional array of table heading data with content and props for each column
 * @param rows - Array of table row data, each containing cells with content and props
 * @param pagination - Whether to show pagination controls in the table footer
 * @param loading - Whether to show loading skeleton instead of table data
 * @param emptyMessage - Text in a single spanning row when not loading and `rows` is empty
 *
 * @example
 * ```tsx
 * const headings = [
 *   { content: "Name", props: { className: "w-full" } },
 *   { content: "Status" }
 * ];
 *
 * const rows = [
 *   {
 *     cells: [
 *       { content: "John Doe" },
 *       { content: <Badge variant="success">Active</Badge> }
 *     ]
 *   }
 * ];
 *
 * <TableFramed headings={headings} rows={rows} loading={false} />
 * ```
 */
// TODO(jeff): Implement table sorting, pagination, and loading states.
// TODO(jeff): Resolve linting issues around array index keys.
export function TableFramed({
    headings,
    rows,
    pagination,
    loading,
    emptyMessage,
}: {
    headings?: TableHeadingData[];
    rows: TableRowData[];
    pagination?: boolean;
    loading?: boolean;
    emptyMessage?: React.ReactNode;
}) {
    const headingCount = headings?.length ?? 0;
    const columnCount = rows[0]?.cells.length ?? 0;
    const effectiveColumnCount = Math.max(headingCount, columnCount, 1);

    let tableBodyContent: React.ReactNode;
    if (loading) {
        tableBodyContent = (
            <TableRow>
                <TableCell colSpan={effectiveColumnCount}>
                    <Skeleton className={cn(TABLE_PLACEHOLDER_ROW_INNER, "justify-around")}>
                        <Spinner className={"size-8 stroke-primary"} />
                    </Skeleton>
                </TableCell>
            </TableRow>
        );
    } else if (rows.length === 0 && emptyMessage != null) {
        tableBodyContent = (
            <TableRow>
                <TableCell colSpan={effectiveColumnCount}>
                    <div
                        className={cn(TABLE_PLACEHOLDER_ROW_INNER, "justify-center text-center text-muted-foreground")}
                    >
                        {emptyMessage}
                    </div>
                </TableCell>
            </TableRow>
        );
    } else {
        tableBodyContent = rows.map((row, rowIndex) => (
            // eslint-disable-next-line react/no-array-index-key
            <TableRow key={rowIndex} {...row.props}>
                {row.cells.map((cell, cellIndex) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <TableCell key={cellIndex} className={"text-left"} {...cell.props}>
                        {cell.content}
                    </TableCell>
                ))}
            </TableRow>
        ));
    }

    return (
        <Frame>
            <FramePanel>
                <Table className={"text-sm sm:text-base md:text-lg"}>
                    {headings && (
                        <TableHeader>
                            <TableRow>
                                {headings.map((heading, headingIndex) => (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <TableHead key={headingIndex} {...heading.props}>
                                        {heading.content}
                                    </TableHead>
                                ))}
                            </TableRow>
                        </TableHeader>
                    )}
                    <TableBody>{tableBodyContent}</TableBody>
                    {pagination && (
                        <TableFooter>
                            <TableRow>
                                <TableCell colSpan={effectiveColumnCount}>
                                    <TablePagination />
                                </TableCell>
                            </TableRow>
                        </TableFooter>
                    )}
                </Table>
            </FramePanel>
        </Frame>
    );
}
